import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface TradingPair {
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
}

/**
 * Fetch trading pairs from an exchange via the pairs API endpoint.
 *
 * Reads `_exchangeId` from triggerPayload — injected by HistoricalPicker's
 * exchange filter form before calling loadData().
 */
export const exchangeListingFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const exchangeId = ctx.triggerPayload._exchangeId as string | undefined;
  if (!exchangeId) return [];

  const response = await authenticatedFetch<{
    code: number;
    message: string;
    data: TradingPair[];
  }>(`${V2_ENDPOINT}/v1/exchanges/${encodeURIComponent(exchangeId)}/pairs`, {
    method: "GET",
  });

  if (!response.ok) throw new Error(`Failed to fetch pairs (HTTP ${response.status})`);
  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.message ?? "Failed to fetch exchange pairs");
  }

  const pairs = data.data ?? [];

  return pairs.map((pair: TradingPair) => ({
    exchange: exchangeId,
    symbol: pair.symbol,
    baseAsset: pair.baseAsset,
    quoteAsset: pair.quoteAsset,
    triggeredAt: new Date().toISOString(),
  }));
};
